import { CONSOLE_DOCS_URL, CONSOLE_REGISTER_URL } from '../config/endpoints.ts';
import { useT } from '../i18n/index.ts';
import { ArrowRight } from './logos/index.tsx';

/**
 * Closing CTA — DESIGN.md §6.10.
 *
 * `bg-butter` full band with a large `claude` blur orb overhead. One
 * short h2 (must fit on one line at desktop; if the copy ever grows
 * past that, change the copy rather than wrapping), a 17px subtitle,
 * and two buttons (primary ink + ghost).
 *
 * This is the last user-visible section before the footer. Keep it
 * brief — a busy closing section undermines the hero's conciseness.
 */
export function ClosingCTA(): JSX.Element {
  const t = useT();
  return (
    <section className="relative overflow-hidden bg-butter py-32" data-testid="closing-cta">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-[720px] w-[720px] -translate-x-1/2 rounded-full bg-claude/10 blur-3xl" />

      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <p className="mb-4 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-claude">
          {t('cta.eyebrow')}
        </p>
        <h2 className="text-[44px] font-semibold leading-[1.1] text-ink md:text-[64px]">
          {t('cta.title.lead')}
          <span className="serif text-claude">{t('cta.title.accent')}</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-[17px] text-ink-muted">{t('cta.subtitle')}</p>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a
            href={CONSOLE_REGISTER_URL}
            className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-[15px] font-medium text-linen shadow-warm-md transition-colors hover:bg-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-claude focus-visible:ring-offset-2 focus-visible:ring-offset-butter"
            data-testid="cta-primary"
          >
            {t('cta.primary')}
            <ArrowRight className="h-[14px] w-[14px]" />
          </a>
          <a
            href={CONSOLE_DOCS_URL}
            className="ghost-strong inline-flex items-center gap-2 rounded-full bg-linen px-8 py-4 text-[15px] font-medium text-ink transition-colors hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-claude focus-visible:ring-offset-2 focus-visible:ring-offset-butter"
            data-testid="cta-secondary"
          >
            {t('cta.secondary')}
          </a>
        </div>
      </div>
    </section>
  );
}
